/**
 * Feed - the reusable vertical video player.
 *
 * Drives a virtualised, scroll-snapping column of <VideoSlide>s: it owns the
 * playback engine, per-slot UI state, neighbour preloading, tap/seek/swipe
 * gestures, double-tap-to-like, reaction handling and (optionally) watch-time
 * tracking + the monetisation overlays.
 *
 * It is rendered twice with different props: the main "For You" channel feed
 * (ads + tracking + channel toggle) and the ad-free "Favorites" feed.
 */
import { Fragment } from 'preact';
import { useState, useRef, useMemo, useEffect } from 'preact/hooks';
import { useHls } from '../hooks/useHls.js';
import { useAdManager } from '../hooks/useAdManager.js';
import { useWatchTracker } from '../hooks/useWatchTracker.js';
import {
  makeUrlBuilders,
  preloadRadius,
  setLastViewed,
  getLastViewed,
  SHARE_URL,
} from '../lib/content.js';
import { loadLikes, saveLikes, getLikeCount, FAVORITE_THRESHOLD } from '../lib/likes.js';
import { getItem, setItem } from '../lib/storage.js';
import { spawnFloatingEmojis, spawnHeartBurst } from '../lib/effects.js';
import { sendEvent, Events, sendVisitOnce } from '../bridge/events.js';
import { setReelMode } from '../bridge/puerInterface.js';
import { VideoSlide } from './VideoSlide.jsx';
import { ChannelToggle } from './ChannelToggle.jsx';
import { AdOverlay } from './AdOverlay.jsx';
import { NotifGate } from './NotifGate.jsx';
import { Loader } from './Loader.jsx';

const DEFAULT_SLOT = {
  ready: false,
  progress: 0,
  paused: false,
  posterFaded: false,
  stalled: false,
  loadPct: 0,
};

const TAP_GUARD_MS = 250;
const SWIPE_THRESHOLD = 12;
const DOUBLE_TAP_MS = 300;
const SCROLL_SETTLE_MS = 200;
const FINGER_HIDE_MS = 3000;

const viewportHeight = () => Math.round(window.innerHeight);

export function Feed({
  videos,
  setVideos,
  feed,
  content,
  loading,
  emptyMessage,
  enableAds = false,
  enableTracking = false,
  showChannelToggle = false,
  isChill = true,
  onToggleChannel,
  favorites,
  showChat = false,
  onRequestChat,
  showEndSlide = false,
  onReachEnd,
  topBar,
}) {
  const containerRef = useRef(null);
  const fxRef = useRef(null);
  const engine = useHls();
  const { videoRefs, isHlsActive, maxSlotCount, hasAnyRef, cleanupSource, ensureSource, playVideo } =
    engine;

  const tracker = useWatchTracker();

  // Gesture timing guards.
  const rafId = useRef(0);
  const lastInteractAt = useRef(0);
  const tapGuardUntil = useRef(0);
  const lastTapAt = useRef(0);
  const swipe = useRef({ active: false, startX: 0, startY: 0, moved: false });
  const settleTimer = useRef(0);

  const [index, setIndex] = useState(0);
  const [slots, setSlots] = useState([]);
  const [settled, setSettled] = useState(true);
  const [vh, setVh] = useState(viewportHeight());
  const [muted, setMuted] = useState(true);
  const [immersive, setImmersive] = useState(() => getItem('immersive') === '1');
  const [didInit, setDidInit] = useState(false);
  const [firstReady, setFirstReady] = useState(false);
  const [radius, setRadius] = useState(preloadRadius);
  // Shown at most once per browser session (flagged in sessionStorage).
  const [showFinger, setShowFinger] = useState(
    () => sessionStorage.getItem('fingerShown') !== '1',
  );
  const [likes, setLikes] = useState(loadLikes);

  const lastHeartFxAt = useRef(0);
  const endTriggered = useRef(false);

  const urls = useMemo(() => makeUrlBuilders(feed), [feed]);

  const { overlayType, checkAndShowAd, closeOverlay } = useAdManager({
    enabled: enableAds,
    onPauseCurrentVideo: () => {
      const el = videoRefs.current[index];
      if (el && !el.paused) {
        el.pause();
        patchSlot(index, { paused: true });
        return true;
      }
      return false;
    },
    onResumeCurrentVideo: () => {
      if (videoRefs.current[index]) {
        patchSlot(index, { paused: false });
        play(index);
      }
    },
  });

  const slotCount = videos.length + (showEndSlide ? 1 : 0);
  const totalHeight = slotCount * vh;
  const onTerminal = showEndSlide && index >= videos.length;

  const visible = useMemo(() => {
    const from = Math.max(0, index - radius);
    const to = Math.min(videos.length - 1, index + radius);
    return videos
      .slice(from, to + 1)
      .map((v, k) => ({ ...v, actualIndex: from + k }));
  }, [videos, index, radius]);

  /* ---------------------------------------------------------------- *
   * Slot-state helper (immutable, no-op when unchanged)
   * ---------------------------------------------------------------- */
  function patchSlot(i, patch) {
    setSlots((prev) => {
      const cur = prev[i] || DEFAULT_SLOT;
      let changed = false;
      for (const k in patch) {
        if (cur[k] !== patch[k]) {
          changed = true;
          break;
        }
      }
      if (!changed) return prev;
      const next = prev.slice();
      next[i] = { ...cur, ...patch };
      return next;
    });
  }

  const play = (i, { markPausedOnFailure = true } = {}) =>
    playVideo(i, {
      markPaused: markPausedOnFailure ? (j) => patchSlot(j, { paused: true }) : null,
      onMutedFallback: () => setMuted(true),
    });

  /* ---------------------------------------------------------------- *
   * Likes + favorites
   * ---------------------------------------------------------------- */
  const likeCountFor = (id) => getLikeCount(likes, id);

  /**
   * A single heart "like" that can be tapped repeatedly. Each tap bumps the
   * persisted per-video counter; the floating-heart animation is throttled so
   * rapid taps don't spam the screen; reaching FAVORITE_THRESHOLD auto-saves.
   */
  function handleLike(actualIndex) {
    const v = videos[actualIndex];
    if (!v) return;
    const nextCount = likeCountFor(v.id) + 1;

    setLikes((prev) => {
      const map = { ...prev, [v.id]: nextCount };
      saveLikes(map);
      return map;
    });

    const now = Date.now();
    if (now - lastHeartFxAt.current > 450) {
      lastHeartFxAt.current = now;
      spawnFloatingEmojis(fxRef.current, '❤️', 1);
    }

    sendEvent(Events.VideoReaction, {
      id: v.id,
      reaction: 'heart',
      active: true,
      content: v.content || content,
    });

    if (nextCount === FAVORITE_THRESHOLD && favorites && !favorites.isFavorite(v.id)) {
      favorites.add(buildSnapshot(v));
      spawnFloatingEmojis(fxRef.current, '⭐', 5);
    }

    checkAndShowAd();
  }

  function buildSnapshot(v) {
    return {
      id: v.id,
      content: v.content || content,
      title: v.title,
      author: v.author,
      caption: v.caption,
      playlist: urls.playlistUrl(v),
      poster: urls.posterUrl(v),
      avatar: urls.avatarUrl(v),
      accent: v.accent,
      reactions: v.reactions,
    };
  }

  function handleToggleFavorite(v) {
    favorites?.toggle(buildSnapshot(v));
  }

  function handleChat(v) {
    onRequestChat?.(v);
  }

  // Immersive mode hides the rail + scrubber + top bar across all slides.
  // The choice is persisted so it carries over to subsequent feeds/sessions.
  function toggleImmersive() {
    setImmersive((v) => {
      const next = !v;
      setItem('immersive', next ? '1' : '0');
      return next;
    });
  }

  async function handleShare() {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Vizos', url: SHARE_URL });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(SHARE_URL);
      }
    } catch {
      /* user cancelled */
    }
  }

  /* ---------------------------------------------------------------- *
   * Media element event handlers (bound per slot at render)
   * ---------------------------------------------------------------- */
  const onCanPlay = (i) => {
    patchSlot(i, { ready: true, stalled: false });
    if (!firstReady && i === index) setFirstReady(true);
  };
  const onPlaying = (i) => patchSlot(i, { posterFaded: true, stalled: false });
  const onWaiting = (i) => patchSlot(i, { stalled: true });
  const onTimeUpdate = (i) => {
    const el = videoRefs.current[i];
    if (!el || !el.duration) return;
    patchSlot(i, { progress: (el.currentTime / el.duration) * 100 });
  };
  const onProgress = (i) => {
    if (isHlsActive(i)) return;
    const el = videoRefs.current[i];
    if (!el || !el.duration || !el.buffered.length) return;
    const end = el.buffered.end(el.buffered.length - 1);
    patchSlot(i, {
      loadPct: Math.min(100, Math.max(0, Math.round((end / el.duration) * 100))),
    });
  };
  const onError = (i) => {
    if (isHlsActive(i)) return;
    const v = videos[i];
    if (!v) return;
    sendEvent(Events.VideoError, {
      id: v.id,
      source: 'media',
      code: videoRefs.current[i]?.error?.code ?? null,
      content,
    });
  };

  /* ---------------------------------------------------------------- *
   * Gestures
   * ---------------------------------------------------------------- */
  function handleTap(i, e) {
    const now = Date.now();
    // Double-tap to like.
    if (now - lastTapAt.current < DOUBLE_TAP_MS && i === index) {
      lastTapAt.current = 0;
      spawnHeartBurst(fxRef.current, e.clientX, e.clientY);
      handleLike(i);
      return;
    }
    lastTapAt.current = now;

    if (now < tapGuardUntil.current || now - lastInteractAt.current < TAP_GUARD_MS) {
      return;
    }
    const el = videoRefs.current[i];
    if (i !== index || !el || !slots[i]?.ready) return;
    checkAndShowAd();
    const nextPaused = !slots[i]?.paused;
    patchSlot(i, { paused: nextPaused });
    if (nextPaused) {
      el.pause();
    } else {
      play(i);
    }
  }

  function handleSeek(i, fraction, committed) {
    const el = videoRefs.current[i];
    if (!el || !el.duration || i !== index) return;
    el.currentTime = fraction * el.duration;
    patchSlot(i, { progress: fraction * 100 });
    if (committed) checkAndShowAd();
  }

  const onPointerDown = (e) => {
    swipe.current = { active: true, startX: e.clientX, startY: e.clientY, moved: false };
  };
  const onPointerMove = (e) => {
    const s = swipe.current;
    if (!s.active || s.moved) return;
    if (
      Math.abs(e.clientX - s.startX) > SWIPE_THRESHOLD ||
      Math.abs(e.clientY - s.startY) > SWIPE_THRESHOLD
    ) {
      s.moved = true;
      tapGuardUntil.current = Date.now() + 300;
    }
  };
  const onPointerUp = () => {
    if (swipe.current.moved) tapGuardUntil.current = Date.now() + 300;
    swipe.current = { active: false, startX: 0, startY: 0, moved: false };
  };

  function handleScroll() {
    lastInteractAt.current = Date.now();
    tapGuardUntil.current = lastInteractAt.current + TAP_GUARD_MS;
    if (settled) setSettled(false);
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => setSettled(true), SCROLL_SETTLE_MS);

    if (rafId.current) return;
    rafId.current = window.requestAnimationFrame(() => {
      rafId.current = 0;
      const el = containerRef.current;
      if (!el || !videos.length) return;
      const maxIndex = videos.length - 1 + (showEndSlide ? 1 : 0);
      const i = Math.max(0, Math.min(maxIndex, Math.round(el.scrollTop / vh)));
      if (i === index) return;
      setIndex(i);

      // Reached the terminal "all caught up" slide of the favorites feed.
      if (showEndSlide && i >= videos.length) {
        if (!endTriggered.current) {
          endTriggered.current = true;
          window.setTimeout(() => onReachEnd?.(), 1300);
        }
        return;
      }

      patchSlot(i, { paused: false });
      if (didInit && enableTracking) {
        const id = videos[i]?.id;
        if (id != null) setLastViewed(content, id);
      }
    });
  }

  const ensureSlot = (i) => {
    const v = videos[i];
    if (!v) return;
    ensureSource({
      index: i,
      playlistUrl: urls.playlistUrl(v),
      onLoadProgress: (pct) => patchSlot(i, { loadPct: pct }),
      onFatalError: (err) =>
        sendEvent(Events.VideoError, {
          id: v.id,
          source: 'hls',
          type: err.type,
          details: err.details,
          reason: err.reason,
          content,
        }),
    });
  };

  /* ---------------------------------------------------------------- *
   * Lifecycle effects
   * ---------------------------------------------------------------- */

  // Mount: native handshake + viewport + connection awareness.
  useEffect(() => {
    if (enableTracking) {
      sendVisitOnce();
      setReelMode(true);
    }
    engine.preloadModule();
    handleResize();
    function handleResizeListener() {
      handleResize();
    }
    window.addEventListener('resize', handleResizeListener);
    const conn = navigator.connection;
    const onConn = () => setRadius(preloadRadius());
    conn?.addEventListener?.('change', onConn);
    return () => {
      window.removeEventListener('resize', handleResizeListener);
      conn?.removeEventListener?.('change', onConn);
      if (rafId.current) window.cancelAnimationFrame(rafId.current);
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    };
  }, []);

  function handleResize() {
    setVh(viewportHeight());
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  }

  // Reset slot states whenever the list length changes.
  useEffect(() => {
    setSlots(Array.from({ length: videos.length }, () => ({ ...DEFAULT_SLOT })));
  }, [videos.length]);

  // Initial scroll position (resume last viewed for the tracked feed).
  useEffect(() => {
    if (!videos.length || didInit) return;
    let start = 0;
    if (enableTracking) {
      const last = getLastViewed(content);
      const found = last ? videos.findIndex((v) => String(v.id) === last) : -1;
      if (last) start = found >= 0 ? found : 0;
      else setLastViewed(content, videos[0].id);
    }
    const raf1 = window.requestAnimationFrame(() => {
      setIndex(start);
      setSlots((prev) => prev.map((s) => (s && s.paused ? { ...s, paused: false } : s)));
      window.requestAnimationFrame(() => {
        if (containerRef.current) containerRef.current.scrollTop = start * vh;
        if (enableTracking) {
          const id = videos[start]?.id;
          if (id != null) setLastViewed(content, id);
        }
        setDidInit(true);
      });
    });
    return () => window.cancelAnimationFrame(raf1);
  }, [videos, content, vh, didInit, enableTracking]);

  // Keep scroll aligned to the active slide when the viewport resizes.
  useEffect(() => {
    if (!didInit) return;
    const el = containerRef.current;
    if (el && videos.length) el.scrollTop = index * vh;
  }, [vh]);

  // Hide the swipe-up finger hint shortly after the first frame is ready, and
  // remember (per session) that it has already been shown so it never repeats.
  useEffect(() => {
    if (!firstReady || !enableTracking || !showFinger) return undefined;
    sessionStorage.setItem('fingerShown', '1');
    const t = window.setTimeout(() => setShowFinger(false), FINGER_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [firstReady, enableTracking, showFinger]);

  /* ---------------- watch-time tracking (tracked feed only) -------- */
  const currentId = videos[index]?.id ?? null;
  const slot = slots[index];

  useEffect(() => {
    if (!enableTracking) return undefined;
    tracker.beginSession({
      videoId: currentId,
      durationMs: (videoRefs.current[index]?.duration || 0) * 1000,
      content,
    });
    return () => tracker.flush();
  }, [currentId, enableTracking]);

  useEffect(() => {
    if (!enableTracking) return;
    const el = videoRefs.current[index];
    if (el?.duration && !Number.isNaN(el.duration)) {
      tracker.updateDuration(el.duration * 1000);
    }
    const pageVisible =
      typeof document === 'undefined' || document.visibilityState === 'visible';
    if (!slot?.paused && !slot?.stalled && !overlayType && slot?.ready && pageVisible) {
      tracker.startPlay();
    } else {
      tracker.pausePlay();
    }
  }, [currentId, slot?.paused, slot?.stalled, slot?.ready, overlayType, index, enableTracking]);

  useEffect(() => {
    if (!enableTracking) return undefined;
    const onHide = () => tracker.flush();
    const onVis = () => document.visibilityState === 'hidden' && tracker.pausePlay();
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enableTracking]);

  // Persist last-viewed on page hide / tab switch (tracked feed only).
  useEffect(() => {
    if (!enableTracking) return undefined;
    const persist = () => {
      const el = containerRef.current;
      if (!el || !videos.length || !vh) return;
      const i = Math.max(0, Math.min(videos.length - 1, Math.round(el.scrollTop / vh)));
      const id = videos[i]?.id;
      if (id != null) setLastViewed(content, id);
    };
    const onHide = () => persist();
    const onVis = () => document.visibilityState === 'hidden' && persist();
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [videos, vh, content, enableTracking]);

  /* ---------------- preload + play orchestration ------------------- */
  useEffect(() => {
    if (!videos.length) return;
    // Free + reset slots that have fallen outside the active window.
    const nextSlots = slots.slice();
    let changed = false;
    const count = maxSlotCount();
    for (let i = 0; i < count; i++) {
      const delta = i - index;
      if (i === index || (delta > 0 && delta <= radius) || !hasAnyRef(i)) continue;
      cleanupSource(i);
      const s = nextSlots[i];
      if (s && (s.ready || s.posterFaded || s.stalled)) {
        nextSlots[i] = { ...s, ready: false, posterFaded: false, stalled: false };
        changed = true;
      }
    }
    if (changed) setSlots(nextSlots);

    // On the terminal "all caught up" slide, nothing should be playing.
    if (onTerminal) {
      videoRefs.current[videos.length - 1]?.pause();
      return;
    }

    visible.forEach((v) => {
      const i = v.actualIndex;
      const el = videoRefs.current[i];
      if (!el) return;
      // While actively scrolling, only the current slot loads.
      if ((!firstReady || !settled) && i !== index) return;
      if (i < index) return;
      ensureSlot(i);
      if (i === index) {
        const ad = checkAndShowAd();
        const s = slots[i];
        if (s?.ready) {
          if (s.paused || ad) el.pause();
          else play(i);
        }
      } else {
        el.pause();
      }
    });
  }, [videos, visible, index, slots, muted, overlayType, feed, firstReady, settled, onTerminal]);

  /* ---------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------- */
  if (!videos.length) {
    return <Loader message={emptyMessage || (loading ? 'Loading ...' : 'Nothing here yet')} />;
  }

  return (
    <Fragment>
      {!immersive && (
        <div class="topbar">
          {showChannelToggle && (
            <ChannelToggle isChill={isChill} onToggle={onToggleChannel} />
          )}
          {topBar}
          <button
            type="button"
            class="topbar__sound"
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? '🔇' : '🔊'}
          </button>
        </div>
      )}

      <div
        class="feed"
        ref={containerRef}
        style={overlayType ? 'filter: blur(20px);' : ''}
        onScroll={handleScroll}
      >
        <div class="feed__rail" style={{ height: `${totalHeight}px` }}>
          {visible.map((v) => (
            <VideoSlide
              key={v.id}
              video={v}
              state={slots[v.actualIndex] || DEFAULT_SLOT}
              isActive={v.actualIndex === index}
              muted={muted}
              top={v.actualIndex * vh}
              poster={urls.posterUrl(v)}
              avatar={urls.avatarUrl(v)}
              accent={v.accent}
              registerRef={(el) => {
                videoRefs.current[v.actualIndex] = el;
              }}
              media={{
                onCanPlay: () => onCanPlay(v.actualIndex),
                onPlaying: () => onPlaying(v.actualIndex),
                onWaiting: () => onWaiting(v.actualIndex),
                onProgress: () => onProgress(v.actualIndex),
                onLoadedMetadata: () => onProgress(v.actualIndex),
                onTimeUpdate: () => onTimeUpdate(v.actualIndex),
                onError: () => onError(v.actualIndex),
              }}
              pointer={{
                onPointerDown,
                onPointerMove,
                onPointerUp,
                onPointerCancel: onPointerUp,
                onClick: (e) => handleTap(v.actualIndex, e),
              }}
              onSeek={(fraction, committed) =>
                handleSeek(v.actualIndex, fraction, committed)
              }
              likeCount={likeCountFor(v.id)}
              isFavorite={favorites?.isFavorite(v.id) || false}
              showChat={showChat}
              immersive={immersive}
              onLike={handleLike}
              onToggleFavorite={handleToggleFavorite}
              onChat={handleChat}
              onShare={handleShare}
              onToggleImmersive={toggleImmersive}
            />
          ))}

          {showEndSlide && (
            <div
              class={`endslide ${onTerminal ? 'endslide--active' : ''}`}
              style={{ transform: `translateY(${videos.length * vh}px)` }}
            >
              <div class="endslide__icon">✓</div>
              <div class="endslide__title">All caught up</div>
              <div class="endslide__sub">Taking you back to For You…</div>
            </div>
          )}
        </div>
      </div>

      <div class="fx-layer" ref={fxRef} />

      {overlayType === 'gate' && <NotifGate onClose={closeOverlay} />}
      {(overlayType === 'appopen' || overlayType === 'interstitial') && (
        <AdOverlay mode={overlayType} onClose={closeOverlay} />
      )}

      {enableTracking && showFinger && firstReady && (
        <img class="finger-hint" src="/finger.webp" alt="" />
      )}
    </Fragment>
  );
}
